import { useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type {
  ExpenseCategory,
  ExpenseItem,
  InvestmentProperty
} from "@aus-property/core";
import { createPropertyTemplate } from "@/data/demoPortfolio";
import { createId } from "@/utils/id";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

const schema = z.object({
  name: z.string().min(2),
  address: z.string().min(5),
  state: z.enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"]),
  kind: z.enum(["HOUSE", "UNIT", "TOWNHOUSE", "DUPLEX"]),
  purchaseDate: z.string().min(4),
  purchasePrice: z.number().min(1),
  deposit: z.number().min(0),
  stampDuty: z.number().min(0),
  interestRate: z.number().min(0).max(20),
  loanType: z.enum(["P_AND_I", "IO"]),
  ioYears: z.number().min(0).max(10),
  weeklyRent: z.number().min(0),
  managementPercent: z.number().min(0).max(100),
  agentFeesAnnual: z.number().min(0),
  rates: z.number().min(0),
  insurance: z.number().min(0),
  maintenance: z.number().min(0),
  strata: z.number().min(0),
  vacancyRate: z.number().min(0).max(100),
  capitalGrowthRate: z.number().min(0).max(20),
  rentGrowthRate: z.number().min(0).max(20),
  expenseInflationRate: z.number().min(0).max(20),
  marginalTaxRate: z.number().min(0).max(47)
});

export type PropertyWizardValues = z.infer<typeof schema>;

interface EditableExpenseItem extends ExpenseItem {
  appliesInYearsText: string;
}

interface EditableCategory {
  id: string;
  name: string;
  items: EditableExpenseItem[];
}

interface PropertyWizardProps {
  initialProperty?: InvestmentProperty;
  onSave: (property: InvestmentProperty) => Promise<void> | void;
  onCancel?: () => void;
  compact?: boolean;
}

const toEditableCategories = (
  categories: ExpenseCategory[]
): EditableCategory[] =>
  categories.map((category) => ({
    ...category,
    items: category.items.map((item) => ({
      ...item,
      appliesInYearsText: (item.appliesInYears ?? []).join(",")
    }))
  }));

const parseYears = (value: string): number[] => {
  const years = value
    .split(",")
    .map((token) => Number(token.trim()))
    .filter((token) => Number.isFinite(token) && token >= 1)
    .map((token) => Math.round(token));

  return Array.from(new Set(years)).sort((a, b) => a - b);
};

const toPersistedCategories = (
  categories: EditableCategory[]
): ExpenseCategory[] =>
  categories
    .map((category) => ({
      id: category.id,
      name: category.name.trim() || "Uncategorized",
      items: category.items
        .map((item) => ({
          id: item.id,
          name: item.name.trim() || "Untitled expense",
          amount: Math.max(item.amount, 0),
          cadence: item.cadence,
          growthRate: item.growthRate,
          appliesInYears:
            item.cadence === "ONE_OFF"
              ? parseYears(item.appliesInYearsText)
              : undefined,
          notes: item.notes
        }))
        .filter((item) => item.amount > 0)
    }))
    .filter((category) => category.items.length > 0);

const toProperty = (
  values: PropertyWizardValues,
  categories: EditableCategory[],
  initialProperty?: InvestmentProperty
): InvestmentProperty => {
  const base = initialProperty ?? createPropertyTemplate();
  const principal = Math.max(values.purchasePrice - values.deposit, 0);

  return {
    ...base,
    id: initialProperty?.id ?? createId("property"),
    name: values.name,
    address: values.address,
    state: values.state,
    kind: values.kind,
    purchase: {
      ...base.purchase,
      purchaseDate: values.purchaseDate,
      price: values.purchasePrice,
      deposit: values.deposit,
      stampDuty: values.stampDuty
    },
    loan: {
      ...base.loan,
      principal,
      interestRate: values.interestRate,
      type: values.loanType,
      ioYears: values.ioYears
    },
    income: {
      ...base.income,
      weeklyRent: values.weeklyRent,
      rentGrowthRate: values.rentGrowthRate
    },
    expenses: {
      ...base.expenses,
      managementPercent: values.managementPercent,
      agentFeesAnnual: values.agentFeesAnnual,
      councilRates: values.rates,
      insurance: values.insurance,
      maintenance: values.maintenance,
      strata: values.strata,
      vacancyRate: values.vacancyRate,
      expenseInflationRate: values.expenseInflationRate,
      categories: toPersistedCategories(categories)
    },
    growth: {
      ...base.growth,
      capitalGrowthRate: values.capitalGrowthRate,
      rentGrowthRate: values.rentGrowthRate,
      expenseInflationRate: values.expenseInflationRate
    },
    tax: {
      ...base.tax,
      marginalTaxRate: values.marginalTaxRate
    },
    createdAt: initialProperty?.createdAt ?? new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
};

const fieldClassName =
  "w-full rounded-lg border border-app bg-app-surface px-3 py-2 text-sm text-app-foreground focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]";

export const PropertyWizard = ({
  initialProperty,
  onSave,
  onCancel,
  compact = false
}: PropertyWizardProps) => {
  const defaults = useMemo(
    () => initialProperty ?? createPropertyTemplate(),
    [initialProperty]
  );
  const [categories, setCategories] = useState<EditableCategory[]>(
    toEditableCategories(defaults.expenses.categories)
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<PropertyWizardValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: defaults.name,
      address: defaults.address,
      state: defaults.state,
      kind: defaults.kind,
      purchaseDate: defaults.purchase.purchaseDate,
      purchasePrice: defaults.purchase.price,
      deposit: defaults.purchase.deposit,
      stampDuty: defaults.purchase.stampDuty,
      interestRate: defaults.loan.interestRate,
      loanType: defaults.loan.type,
      ioYears: defaults.loan.ioYears,
      weeklyRent: defaults.income.weeklyRent,
      managementPercent: defaults.expenses.managementPercent,
      agentFeesAnnual: defaults.expenses.agentFeesAnnual,
      rates: defaults.expenses.councilRates,
      insurance: defaults.expenses.insurance,
      maintenance: defaults.expenses.maintenance,
      strata: defaults.expenses.strata,
      vacancyRate: defaults.expenses.vacancyRate,
      capitalGrowthRate: defaults.growth.capitalGrowthRate,
      rentGrowthRate: defaults.growth.rentGrowthRate,
      expenseInflationRate: defaults.growth.expenseInflationRate,
      marginalTaxRate: defaults.tax.marginalTaxRate
    }
  });

  const addCategory = () => {
    setCategories((current) => [
      ...current,
      {
        id: createId("cat"),
        name: "New Category",
        items: []
      }
    ]);
  };

  const addItem = (categoryId: string) => {
    setCategories((current) =>
      current.map((category) =>
        category.id !== categoryId
          ? category
          : {
              ...category,
              items: [
                ...category.items,
                {
                  id: createId("item"),
                  name: "",
                  amount: 0,
                  cadence: "ANNUAL",
                  growthRate: undefined,
                  appliesInYears: undefined,
                  appliesInYearsText: "",
                  notes: ""
                }
              ]
            }
      )
    );
  };

  return (
    <Card className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold">
          {initialProperty ? "Edit Property" : "Create Property"}
        </h3>
        <p className="text-sm text-app-soft">
          Define purchase assumptions, agent fees, and flexible expense
          collections.
        </p>
      </div>

      <form
        className="grid grid-cols-1 gap-3 md:grid-cols-2"
        onSubmit={handleSubmit(async (values) => {
          await onSave(toProperty(values, categories, initialProperty));
        })}
      >
        <label className="space-y-1 text-sm md:col-span-2">
          <span>Name</span>
          <input className={fieldClassName} {...register("name")} />
          {errors.name ? (
            <span className="text-xs text-red-500">Name is required</span>
          ) : null}
        </label>

        <label className="space-y-1 text-sm md:col-span-2">
          <span>Address</span>
          <input className={fieldClassName} {...register("address")} />
        </label>

        <label className="space-y-1 text-sm">
          <span>State</span>
          <select className={fieldClassName} {...register("state")}>
            {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"].map(
              (state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              )
            )}
          </select>
        </label>

        <label className="space-y-1 text-sm">
          <span>Type</span>
          <select className={fieldClassName} {...register("kind")}>
            {[
              ["HOUSE", "House"],
              ["UNIT", "Unit"],
              ["TOWNHOUSE", "Townhouse"],
              ["DUPLEX", "Duplex"]
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 text-sm">
          <span>Purchase date</span>
          <input
            className={fieldClassName}
            type="date"
            {...register("purchaseDate")}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Purchase price</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("purchasePrice", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Deposit</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("deposit", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Stamp duty</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("stampDuty", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Interest rate (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.01"
            {...register("interestRate", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Loan type</span>
          <select className={fieldClassName} {...register("loanType")}>
            <option value="P_AND_I">P&I</option>
            <option value="IO">Interest only</option>
          </select>
        </label>

        <label className="space-y-1 text-sm">
          <span>IO years</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("ioYears", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Weekly rent</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("weeklyRent", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Management fee (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("managementPercent", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Agent fees (fixed annual)</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("agentFeesAnnual", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Council rates (annual)</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("rates", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Insurance (annual)</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("insurance", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Maintenance (annual)</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("maintenance", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Strata (annual)</span>
          <input
            className={fieldClassName}
            type="number"
            {...register("strata", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Vacancy (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("vacancyRate", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Capital growth (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("capitalGrowthRate", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Rent growth (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("rentGrowthRate", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Expense inflation (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("expenseInflationRate", { valueAsNumber: true })}
          />
        </label>

        <label className="space-y-1 text-sm">
          <span>Marginal tax rate (%)</span>
          <input
            className={fieldClassName}
            type="number"
            step="0.1"
            {...register("marginalTaxRate", { valueAsNumber: true })}
          />
        </label>

        <div className="space-y-3 md:col-span-2">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-semibold">Expense Collections</h4>
            <Button type="button" variant="secondary" onClick={addCategory}>
              Add Category
            </Button>
          </div>

          {categories.map((category) => (
            <div
              key={category.id}
              className="space-y-2 rounded-xl border border-app bg-app-surface-muted p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <input
                  className={`${fieldClassName} max-w-xs`}
                  value={category.name}
                  onChange={(event) => {
                    setCategories((current) =>
                      current.map((existing) =>
                        existing.id === category.id
                          ? { ...existing, name: event.target.value }
                          : existing
                      )
                    );
                  }}
                  aria-label="Category name"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => addItem(category.id)}
                >
                  Add Item
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => {
                    setCategories((current) =>
                      current.filter((existing) => existing.id !== category.id)
                    );
                  }}
                >
                  Remove Category
                </Button>
              </div>

              {category.items.map((item) => (
                <div
                  key={item.id}
                  className="grid grid-cols-1 gap-2 rounded-lg border border-app bg-app-surface p-2 md:grid-cols-5"
                >
                  <input
                    className={fieldClassName}
                    placeholder="Item"
                    value={item.name}
                    onChange={(event) => {
                      setCategories((current) =>
                        current.map((existingCategory) =>
                          existingCategory.id !== category.id
                            ? existingCategory
                            : {
                                ...existingCategory,
                                items: existingCategory.items.map(
                                  (existingItem) =>
                                    existingItem.id === item.id
                                      ? {
                                          ...existingItem,
                                          name: event.target.value
                                        }
                                      : existingItem
                                )
                              }
                        )
                      );
                    }}
                  />

                  <input
                    className={fieldClassName}
                    type="number"
                    placeholder="Amount"
                    value={item.amount}
                    onChange={(event) => {
                      setCategories((current) =>
                        current.map((existingCategory) =>
                          existingCategory.id !== category.id
                            ? existingCategory
                            : {
                                ...existingCategory,
                                items: existingCategory.items.map(
                                  (existingItem) =>
                                    existingItem.id === item.id
                                      ? {
                                          ...existingItem,
                                          amount: Number(event.target.value)
                                        }
                                      : existingItem
                                )
                              }
                        )
                      );
                    }}
                  />

                  <select
                    className={fieldClassName}
                    value={item.cadence}
                    onChange={(event) => {
                      setCategories((current) =>
                        current.map((existingCategory) =>
                          existingCategory.id !== category.id
                            ? existingCategory
                            : {
                                ...existingCategory,
                                items: existingCategory.items.map(
                                  (existingItem) =>
                                    existingItem.id === item.id
                                      ? {
                                          ...existingItem,
                                          cadence: event.target
                                            .value as ExpenseItem["cadence"]
                                        }
                                      : existingItem
                                )
                              }
                        )
                      );
                    }}
                  >
                    <option value="MONTHLY">Monthly</option>
                    <option value="ANNUAL">Annual</option>
                    <option value="ONE_OFF">One-off</option>
                  </select>

                  <input
                    className={fieldClassName}
                    type="number"
                    step="0.1"
                    placeholder="Growth %"
                    value={item.growthRate ?? ""}
                    onChange={(event) => {
                      const value = event.target.value;
                      setCategories((current) =>
                        current.map((existingCategory) =>
                          existingCategory.id !== category.id
                            ? existingCategory
                            : {
                                ...existingCategory,
                                items: existingCategory.items.map(
                                  (existingItem) =>
                                    existingItem.id === item.id
                                      ? {
                                          ...existingItem,
                                          growthRate:
                                            value === ""
                                              ? undefined
                                              : Number(value)
                                        }
                                      : existingItem
                                )
                              }
                        )
                      );
                    }}
                  />

                  <div className="flex gap-2">
                    <input
                      className={fieldClassName}
                      placeholder="Years e.g. 3,7"
                      value={item.appliesInYearsText}
                      onChange={(event) => {
                        setCategories((current) =>
                          current.map((existingCategory) =>
                            existingCategory.id !== category.id
                              ? existingCategory
                              : {
                                  ...existingCategory,
                                  items: existingCategory.items.map(
                                    (existingItem) =>
                                      existingItem.id === item.id
                                        ? {
                                            ...existingItem,
                                            appliesInYearsText:
                                              event.target.value
                                          }
                                        : existingItem
                                  )
                                }
                          )
                        );
                      }}
                      disabled={item.cadence !== "ONE_OFF"}
                    />
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => {
                        setCategories((current) =>
                          current.map((existingCategory) =>
                            existingCategory.id !== category.id
                              ? existingCategory
                              : {
                                  ...existingCategory,
                                  items: existingCategory.items.filter(
                                    (existingItem) =>
                                      existingItem.id !== item.id
                                  )
                                }
                          )
                        );
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 md:col-span-2">
          <Button type="submit" disabled={isSubmitting}>
            {initialProperty ? "Update Property" : "Save Property"}
          </Button>
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
      </form>

      {!compact ? (
        <p className="text-xs text-app-soft">
          Use one-off items for irregular large repairs (for example years 3, 7,
          and 12).
        </p>
      ) : null}
    </Card>
  );
};
